import { z } from "zod";

/**
 * Strukturierte Vitalwerte mit Plausibilitätsprüfung (ARCHITEKTUR §2.4 R12).
 * Alle Grenzwerte sind ein VORSCHLAG und ärztlich festzulegen – deshalb zentral hier.
 */

export const VITAL_TYPES = ["rr", "puls", "temp", "spo2", "blutzucker", "schnelltest"] as const;
export type VitalType = (typeof VITAL_TYPES)[number];

export const SCHNELLTEST_ERGEBNISSE = ["positiv", "negativ", "ungueltig"] as const;
export const MMOL_TO_MGDL = 18.016;

export const vitalInputSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("rr"), sys: z.number().int(), dia: z.number().int() }),
  z.object({ type: z.literal("puls"), value: z.number().int() }),
  z.object({ type: z.literal("temp"), value: z.number() }),
  z.object({ type: z.literal("spo2"), value: z.number().int() }),
  z.object({ type: z.literal("blutzucker"), value: z.number(), unit: z.enum(["mg/dl", "mmol/l"]) }),
  z.object({
    type: z.literal("schnelltest"),
    test: z.string().trim().min(1).max(60),
    ergebnis: z.enum(SCHNELLTEST_ERGEBNISSE),
  }),
]);
export type VitalInput = z.infer<typeof vitalInputSchema>;

export type VitalWarning =
  | "rr_hoch"
  | "rr_niedrig"
  | "dia_hoch"
  | "puls_niedrig"
  | "puls_hoch"
  | "fieber"
  | "untertemperatur"
  | "spo2_niedrig"
  | "bz_niedrig"
  | "bz_hoch"
  | "test_positiv";

export type VitalRejection = "out_of_range" | "sys_not_above_dia";

export type VitalCheck =
  | { ok: true; level: "ok" | "warnung"; warnings: VitalWarning[]; normalized: VitalInput }
  | { ok: false; error: VitalRejection };

export const VITAL_LIMITS = {
  rr: { sys: [50, 300], dia: [20, 200], warnSysHigh: 180, warnSysLow: 90, warnDiaHigh: 110 },
  puls: { range: [20, 250], warnLow: 45, warnHigh: 120 },
  temp: { range: [30, 44], warnHigh: 39, warnLow: 35.5 },
  spo2: { range: [50, 100], warnLow: 92 },
  blutzucker: { range: [10, 800], warnLow: 70, warnHigh: 250 },
} as const;

const inRange = (v: number, [lo, hi]: readonly [number, number]) => Number.isFinite(v) && v >= lo && v <= hi;

export function checkVital(input: VitalInput): VitalCheck {
  const w: VitalWarning[] = [];
  const done = (normalized: VitalInput): VitalCheck => ({
    ok: true,
    level: w.length ? "warnung" : "ok",
    warnings: w,
    normalized,
  });
  switch (input.type) {
    case "rr": {
      const L = VITAL_LIMITS.rr;
      if (!inRange(input.sys, L.sys) || !inRange(input.dia, L.dia))
        return { ok: false, error: "out_of_range" };
      if (input.sys <= input.dia) return { ok: false, error: "sys_not_above_dia" };
      if (input.sys >= L.warnSysHigh) w.push("rr_hoch");
      if (input.sys < L.warnSysLow) w.push("rr_niedrig");
      if (input.dia >= L.warnDiaHigh) w.push("dia_hoch");
      return done(input);
    }
    case "puls": {
      const L = VITAL_LIMITS.puls;
      if (!inRange(input.value, L.range)) return { ok: false, error: "out_of_range" };
      if (input.value < L.warnLow) w.push("puls_niedrig");
      if (input.value > L.warnHigh) w.push("puls_hoch");
      return done(input);
    }
    case "temp": {
      const L = VITAL_LIMITS.temp;
      if (!inRange(input.value, L.range)) return { ok: false, error: "out_of_range" };
      if (input.value >= L.warnHigh) w.push("fieber");
      if (input.value < L.warnLow) w.push("untertemperatur");
      return done({ ...input, value: Math.round(input.value * 10) / 10 });
    }
    case "spo2": {
      const L = VITAL_LIMITS.spo2;
      if (!inRange(input.value, L.range)) return { ok: false, error: "out_of_range" };
      if (input.value < L.warnLow) w.push("spo2_niedrig");
      return done(input);
    }
    case "blutzucker": {
      const L = VITAL_LIMITS.blutzucker;
      const mgdl = Math.round(input.unit === "mmol/l" ? input.value * MMOL_TO_MGDL : input.value);
      if (!inRange(mgdl, L.range)) return { ok: false, error: "out_of_range" };
      if (mgdl < L.warnLow) w.push("bz_niedrig");
      if (mgdl > L.warnHigh) w.push("bz_hoch");
      return done({ type: "blutzucker", value: mgdl, unit: "mg/dl" });
    }
    case "schnelltest":
      if (input.ergebnis === "positiv") w.push("test_positiv");
      return done(input);
  }
}
